import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useCallback, useRef, useState } from 'react';
import { TRASH_DROPPABLE_ID } from '../../lib/drag-position.js';

/**
 * Board-Liste per Ziehen umsortieren oder in den Papierkorb legen
 * (SPEC-Ergaenzung). Vereinfachte Variante von `useBoardDnd`: eine flache
 * Liste statt Spalten, deshalb reicht die Standard-Sortierlogik von
 * `@dnd-kit/sortable` statt einer eigenen Index-Berechnung.
 */
export function useBoardListDnd({
  boardIds,
  onReorder,
  onTrash,
}: {
  boardIds: readonly string[];
  onReorder: (orderedIds: string[]) => void;
  /** Ablegen im Papierkorb — archiviert das Board (SPEC 5.2, auf Boards uebertragen). */
  onTrash: (boardId: string) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overTrash, setOverTrash] = useState(false);
  /** Nur bei Finger/Maus wird der Papierkorb eingeblendet, per Tastatur ist er nicht erreichbar. */
  const [pointerDrag, setPointerDrag] = useState(false);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const trackPointer = useRef((event: PointerEvent) => {
    pointer.current = { x: event.clientX, y: event.clientY };
  }).current;

  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /*
   * Derselbe Kunstgriff wie bei den Karten: der Papierkorb liegt ueber der
   * Liste, und welches der beiden Rechtecke `closestCenter` traefe, haengt
   * sonst an Groessenverhaeltnissen statt an der Absicht. Per Tastatur gibt
   * es keinen Zeiger — dort faellt der Papierkorb aus den Kandidaten.
   */
  const collisionDetection: CollisionDetection = useCallback((args) => {
    if (pointer.current === null) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => container.id !== TRASH_DROPPABLE_ID,
        ),
      });
    }

    const trash = args.droppableRects.get(TRASH_DROPPABLE_ID);
    const { x, y } = pointer.current;
    if (trash && x >= trash.left && x <= trash.right && y >= trash.top && y <= trash.bottom) {
      const container = args.droppableContainers.find(
        (candidate) => candidate.id === TRASH_DROPPABLE_ID,
      );
      if (container) {
        return [{ id: TRASH_DROPPABLE_ID, data: { droppableContainer: container, value: 0 } }];
      }
    }

    return closestCenter(args);
  }, []);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(String(event.active.id));
      pointer.current = null;
      setPointerDrag(!(event.activatorEvent instanceof KeyboardEvent));
      window.addEventListener('pointermove', trackPointer);
      navigator.vibrate?.(10);
    },
    [trackPointer],
  );

  const onDragMove = useCallback((event: DragMoveEvent) => {
    setOverTrash(event.over?.id === TRASH_DROPPABLE_ID);
  }, []);

  const reset = useCallback(() => {
    window.removeEventListener('pointermove', trackPointer);
    pointer.current = null;
    setActiveId(null);
    setOverTrash(false);
    setPointerDrag(false);
  }, [trackPointer]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const boardId = String(event.active.id);
      const landedOnTrash = overTrash;
      const overId = event.over ? String(event.over.id) : null;
      reset();

      if (landedOnTrash) {
        onTrash(boardId);
        return;
      }
      if (!overId || overId === boardId) return;

      const oldIndex = boardIds.indexOf(boardId);
      const newIndex = boardIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder(arrayMove([...boardIds], oldIndex, newIndex));
    },
    [boardIds, onReorder, onTrash, overTrash, reset],
  );

  return {
    activeId,
    overTrash,
    showTrash: activeId !== null && pointerDrag,
    dndContextProps: {
      sensors,
      collisionDetection,
      onDragStart,
      onDragMove,
      onDragOver: onDragMove,
      onDragEnd,
      onDragCancel: reset,
    },
  };
}
